import React, { useState, useEffect } from 'react';
import {
    CheckCircle2, XCircle, AlertCircle, Clock,
    Search, Filter, ExternalLink, MessageSquare,
    MoreVertical, FileText, Activity, ShieldAlert,
    Bot
} from 'lucide-react';
import { apiFetch } from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

// Roles that can see tasks across all tenants (support agents + platform admins)
const CROSS_TENANT_ROLES = new Set(['r1', 'r2', 'r5', 'r6', 'r8', 'r9', 'r10']);

const TaskCenterPage = ({ token, currentUser }) => {
    const { formatDate } = useTimezoneFormatter();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('My Inbox');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('created_desc');

    const [selectedTask, setSelectedTask] = useState(null);
    const [rightPaneTab, setRightPaneTab] = useState('overview'); // overview, ai_copilot

    const [actionLoading, setActionLoading] = useState(false);

    // Fetch Tasks
    const fetchTasks = async () => {
        setLoading(true);
        try {
            // For now, fetch ALL tasks if they want filtering on the frontend
            // or pass status=PENDING as a query param.
            const response = await apiFetch('/tasks', token);
            setTasks(response);
            if (response.length > 0 && !selectedTask) {
                // Auto-select first pending task
                const firstPending = response.find(t => t.status === 'PENDING') || response[0];
                handleSelectTask(firstPending.id);
            }
        } catch (error) {
            console.error("Failed to fetch tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchTasks();
    }, [token]);

    const handleSelectTask = async (taskId) => {
        try {
            const taskDetails = await apiFetch(`/tasks/${taskId}`, token);
            setSelectedTask(taskDetails);
        } catch (error) {
            console.error("Failed to fetch task details:", error);
        }
    };

    const handleAction = async (actionStr) => {
        if (!selectedTask) return;
        setActionLoading(true);
        try {
            await apiFetch(`/tasks/${selectedTask.id}/action`, token, {
                method: 'POST',
                body: JSON.stringify({ action: actionStr, comment: "" })
            });
            // Refresh list
            await fetchTasks();
            setSelectedTask(null); // Clear selection or select next
        } catch (e) {
            console.error(`Failed to ${actionStr}:`, e);
        } finally {
            setActionLoading(false);
        }
    };

    // Derived counts
    const pendingCount = tasks.filter(t => t.status === 'PENDING').length;
    const urgentCount = tasks.filter(t => t.status === 'PENDING' && (t.title.toLowerCase().includes('urgent') || t.timeout_hours > 0)).length;

    // Filtering logic
    const filteredTasks = tasks.filter(t => {
        if (activeTab === 'My Inbox') return t.status === 'PENDING';
        if (activeTab === 'Urgent') return t.status === 'PENDING' && (t.title.toLowerCase().includes('urgent') || t.timeout_hours > 0);
        if (activeTab === 'Completed') return ['APPROVED', 'REJECTED', 'ESCALATED'].includes(t.status);
        if (activeTab === 'All Tasks') return true;
        return true;
    }).filter(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()) || (t.description || '').toLowerCase().includes(searchQuery.toLowerCase()));
    const sortedTasks = [...filteredTasks].sort((a, b) => {
        const createdA = new Date(a.created_at || 0).getTime();
        const createdB = new Date(b.created_at || 0).getTime();
        if (sortOption === 'created_desc') return createdB - createdA;
        if (sortOption === 'created_asc') return createdA - createdB;
        if (sortOption === 'title_asc') return (a.title || '').localeCompare(b.title || '');
        if (sortOption === 'title_desc') return (b.title || '').localeCompare(a.title || '');
        return 0;
    });

    return (
        <div className="flex h-full bg-gray-50 dark:bg-gray-900 overflow-hidden font-sans">

            {/* 1. LEFT NAVIGATION PANE */}
            <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col items-stretch shrink-0 overflow-y-auto">
                <div className="p-6 pb-2">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Task Center</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Manage approvals & human tasks</p>
                </div>

                <div className="px-4 py-4 space-y-1">
                    {[
                        { name: 'My Inbox', icon: <Clock className="w-4 h-4" />, count: pendingCount, activeColor: 'bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
                        { name: 'Urgent', icon: <ShieldAlert className="w-4 h-4" />, count: urgentCount, activeColor: 'bg-red-50 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
                        { name: 'Completed', icon: <CheckCircle2 className="w-4 h-4" />, count: null, activeColor: 'bg-green-50 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
                        { name: 'All Tasks', icon: <Activity className="w-4 h-4" />, count: tasks.length, activeColor: 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100' },
                    ].map(tab => (
                        <button
                            key={tab.name}
                            onClick={() => setActiveTab(tab.name)}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors
                                ${activeTab === tab.name
                                    ? tab.activeColor
                                    : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                        >
                            <div className="flex items-center gap-3">
                                {tab.icon}
                                {tab.name}
                            </div>
                            {tab.count !== null && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.name ? 'bg-white bg-opacity-60 dark:bg-gray-800' : 'bg-gray-100 dark:bg-gray-700'
                                    }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* 2. TASK LIST PANE */}
            <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                            />
                        </div>
                        <select
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value)}
                            className="px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs dark:bg-gray-900 dark:border-gray-700 dark:text-white"
                        >
                            <option value="created_desc">Newest</option>
                            <option value="created_asc">Oldest</option>
                            <option value="title_asc">Title A-Z</option>
                            <option value="title_desc">Title Z-A</option>
                        </select>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 flex justify-center"><LoadingSpinner /></div>
                    ) : sortedTasks.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">No tasks found.</div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                            {sortedTasks.map(task => (
                                <div
                                    key={task.id}
                                    onClick={() => handleSelectTask(task.id)}
                                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors relative border-l-4 ${selectedTask?.id === task.id ? 'bg-blue-50/50 dark:bg-blue-900/20 border-blue-500' : 'border-transparent'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="flex flex-col truncate pr-4">
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{task.title}</h3>
                                            {task.timeout_hours > 0 && <span className="text-[10px] text-red-500 font-medium flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {task.timeout_hours}h Timeout</span>}
                                        </div>
                                        <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider
                                            ${task.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                                                task.status === 'APPROVED' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                                                    task.status === 'REJECTED' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                                                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}
                                        >
                                            {task.status}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">{task.description}</p>
                                    <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                                        <div className="flex items-center">
                                            <Clock className="w-3 h-3 mr-1" />
                                            {formatDate(task.created_at)}
                                        </div>
                                        {CROSS_TENANT_ROLES.has(currentUser?.roleId) && task.tenant_id && (
                                            <span className="text-[10px] bg-purple-50 text-purple-600 dark:bg-purple-900/50 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono">
                                                {task.tenant_id.replace('t_', '')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 3 & 4. CONTEXT / CONTENT PANE & ACTION BAR */}
            <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 overflow-hidden relative">
                {selectedTask ? (
                    <>
                        {/* 4. Action Bar */}
                        <div className="h-16 px-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0 bg-white dark:bg-gray-900">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{selectedTask.title}</h2>
                                {selectedTask.timeout_hours > 0 && (
                                    <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm border border-red-200 dark:border-red-800">
                                        <Clock className="w-3 h-3" /> {selectedTask.timeout_hours}h Timeout
                                    </span>
                                )}
                                {CROSS_TENANT_ROLES.has(currentUser?.roleId) && selectedTask.tenant_id && (
                                    <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:border-purple-800 px-2 py-0.5 rounded font-mono">
                                        Tenant: {selectedTask.tenant_id}
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {selectedTask.status === 'PENDING' && (
                                    <>
                                        <button
                                            onClick={() => handleAction('rejected')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Reject
                                        </button>
                                        <button
                                            onClick={() => handleAction('escalated')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-800 rounded-lg text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/80 transition-colors"
                                        >
                                            Escalate
                                        </button>
                                        <button
                                            onClick={() => handleAction('approved')}
                                            disabled={actionLoading}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                                        >
                                            {actionLoading ? "Processing..." : "Approve"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Middle Nav Tabs */}
                        <div className="px-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                            <div className="flex space-x-6">
                                <button
                                    onClick={() => setRightPaneTab('overview')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors ${rightPaneTab === 'overview' ? 'border-primary text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    Overview
                                </button>
                                <button
                                    onClick={() => setRightPaneTab('ai_copilot')}
                                    className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${rightPaneTab === 'ai_copilot' ? 'border-purple-500 text-purple-700 dark:text-purple-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <Bot className="w-4 h-4" />
                                    AI Copilot
                                </button>
                            </div>
                        </div>

                        {/* Details Content */}
                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30 dark:bg-gray-900/50">
                            {rightPaneTab === 'overview' ? (
                                <div className="max-w-4xl mx-auto space-y-6">
                                    {/* Description Card */}
                                    {selectedTask.description && (
                                        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Instructions</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{selectedTask.description}</p>
                                        </div>
                                    )}

                                    {/* Context Data Grid */}
                                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/80 flex justify-between items-center">
                                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Context Data</h3>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">Flow ID: {selectedTask.flow_id?.split('-')[0]}</span>
                                        </div>
                                        <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-6">
                                            {Object.entries(selectedTask.context_data || {}).map(([key, value]) => {
                                                // Ignore internal DuckDB route variables if any leak
                                                if (key.startsWith('_')) return null;
                                                return (
                                                    <div key={key}>
                                                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                                            {key}
                                                        </label>
                                                        <div className="text-sm text-gray-900 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 rounded border border-gray-100 dark:border-gray-700 break-words whitespace-pre-wrap">
                                                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {Object.keys(selectedTask.context_data || {}).length === 0 && (
                                                <div className="col-span-2 text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                                    No context data provided by the workflow.
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {selectedTask.status !== 'PENDING' && (
                                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-700">
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                This task was marked as <strong className="dark:text-white">{selectedTask.status}</strong> on {formatDate(selectedTask.resolved_at)}. No further actions are required.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="max-w-4xl mx-auto h-full flex flex-col">
                                    <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-purple-100 dark:border-purple-900/50 shadow-sm overflow-hidden flex flex-col justify-center items-center text-center p-8">
                                        <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center mb-4 border border-purple-100 dark:border-purple-900/50">
                                            <Bot className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">AI Copilot Analysis</h3>
                                        <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md">
                                            This feature connects your custom AI agents to analyze the context data and provide a recommendation. It will be available in a future update.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50/50 dark:bg-gray-900 px-8 text-center">
                        <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-full shadow-sm flex items-center justify-center mb-4 border border-gray-100 dark:border-gray-700">
                            <CheckCircle2 className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">You're all caught up!</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">Select a task from the list to view its details, context data, and take action.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskCenterPage;
